/**
 * Referral Page — real balance from DB, friend list, withdrawal request.
 * Referral code gives 10% discount on monthly plan.
 * Each referred premium purchase = 2,000 UZS bonus.
 * Withdrawal: Premium users, 100K+ balance, manual admin approval.
 */

import React, { useCallback, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Copy, Share2, Gift, Users, ArrowDownToLine,
  Wallet, AlertCircle, CreditCard,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import { LoadingSpinner } from '@turnifly/shared/ui'
import { useAuthStore } from '@turnifly/shared/stores'
import { useProfile } from '@turnifly/shared/hooks'
import {
  useReferralBalance,
  useReferralFriends,
  useRequestWithdrawal,
} from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact, triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { format } from 'date-fns'

interface ReferralPageProps {
  isOpen: boolean
  onClose: () => void
}

export const ReferralPage: React.FC<ReferralPageProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { data: profile } = useProfile(user?.id)
  const { data: balance, isLoading: balanceLoading } = useReferralBalance(isOpen ? user?.id : undefined)
  const { data: friends, isLoading: friendsLoading } = useReferralFriends(isOpen ? user?.id : undefined)
  const withdrawMutation = useRequestWithdrawal()

  const [showWithdrawForm, setShowWithdrawForm] = useState(false)
  const [withdrawCardNumber, setWithdrawCardNumber] = useState('')
  const [withdrawCardHolder, setWithdrawCardHolder] = useState('')
  const [withdrawError, setWithdrawError] = useState('')
  const [friendsTab, setFriendsTab] = useState<'all' | 'monthly'>('all')

  const referralCode = profile?.referral_code ?? 'TURNI-XXXXX'
  const referralLink = `https://t.me/turnifly_bot?start=ref_${referralCode}`
  const isPremium = profile?.is_premium ?? false
  const currentBalance = balance?.balance ?? 0
  const canWithdraw = isPremium && currentBalance >= 100000

  const handleCopy = useCallback(async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text)
      hapticNotification('success')
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      hapticNotification('success')
    }
  }, [])

  const handleShare = useCallback((): void => {
    const shareText = t('referral.shareText', { code: referralCode })
    const url = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(shareText)}`
    window.open(url, '_blank', 'noopener')
  }, [referralCode, referralLink, t])

  const handleWithdraw = (): void => {
    const cleanCard = withdrawCardNumber.replace(/\s/g, '')
    if (cleanCard.length !== 16) {
      setWithdrawError('Karta raqami 16 ta raqam bo\'lishi kerak')
      return
    }
    if (withdrawCardHolder.trim().length < 3) {
      setWithdrawError('Ism familiyani kiriting')
      return
    }

    hapticImpact('medium')
    setWithdrawError('')

    withdrawMutation.mutate(
      { cardNumber: cleanCard, cardHolder: withdrawCardHolder.trim() },
      {
        onSuccess: () => {
          hapticNotification('success')
          setShowWithdrawForm(false)
          setWithdrawCardNumber('')
          setWithdrawCardHolder('')
        },
        onError: (err) => {
          setWithdrawError(err.message)
          hapticNotification('error')
        },
      },
    )
  }

  const formatPrice = (amount: number): string => amount.toLocaleString('uz-UZ')

  const formatCardInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 16)
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
  }

  const filteredFriends = friendsTab === 'monthly'
    ? (friends ?? []).filter((f) => f.status === 'active')
    : (friends ?? [])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ y: '100%', opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh]
              lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
              lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-md
              lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            {/* Header — safe area */}
            <div className="flex items-center justify-between px-5 pb-2 shrink-0" style={{ paddingTop: 'max(20px, calc(var(--tg-safe-area-inset-top) + 8px))' }}>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04]">
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{t('referral.title')}</h2>
              <div className="w-10" />
            </div>

            <div className="flex-1 overflow-y-auto px-5" style={{ paddingBottom: 'max(32px, calc(var(--tg-safe-area-inset-bottom) + 16px))' }}>
              {/* Balance Card */}
              <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-accent/5 border border-primary/15 p-5 text-center">
                <p className="text-xs text-text-hint mb-1">{t('referral.balance')}</p>

                {balanceLoading ? (
                  <div className="py-2"><LoadingSpinner size="sm" /></div>
                ) : (
                  <p className="text-3xl font-display font-bold text-text">
                    {formatPrice(currentBalance)}
                    <span className="text-lg text-text-hint ml-1">UZS</span>
                  </p>
                )}

                <div className="flex items-center justify-center gap-4 mt-3">
                  <div className="text-center">
                    <p className="text-sm font-semibold text-text">
                      {formatPrice(balance?.total_earned ?? 0)}
                    </p>
                    <p className="text-[10px] text-text-hint">{t('referral.earned')}</p>
                  </div>
                  <div className="w-px h-6 bg-white/[0.06]" />
                  <div className="text-center">
                    <p className="text-sm font-semibold text-text">
                      {formatPrice(balance?.total_withdrawn ?? 0)}
                    </p>
                    <p className="text-[10px] text-text-hint">{t('referral.withdrawn')}</p>
                  </div>
                  <div className="w-px h-6 bg-white/[0.06]" />
                  <div className="text-center">
                    <p className="text-sm font-semibold text-text">{friends?.length ?? 0}</p>
                    <p className="text-[10px] text-text-hint">{t('referral.friends')}</p>
                  </div>
                </div>

                <Button
                  variant="glass"
                  size="sm"
                  className="mt-4"
                  disabled={!canWithdraw}
                  onClick={() => {
                    setShowWithdrawForm(true)
                    hapticImpact('light')
                  }}
                >
                  <ArrowDownToLine size={14} className="mr-1.5" />
                  {t('referral.withdraw')}
                </Button>
                <p className="text-[10px] text-text-hint mt-1.5">
                  {!isPremium
                    ? 'Premium kerak'
                    : currentBalance < 100000
                    ? t('referral.minWithdraw')
                    : ''}
                </p>
              </div>

              {/* Withdrawal Form */}
              <AnimatePresence>
                {showWithdrawForm && canWithdraw && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Wallet size={16} className="text-primary" />
                        <span className="text-sm font-medium text-text">
                          Yechib olish: {formatPrice(currentBalance)} UZS
                        </span>
                      </div>
                      <div>
                        <label className="text-xs text-text-hint mb-1 block">Karta raqami</label>
                        <div className="relative">
                          <CreditCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint/40" />
                          <input
                            type="tel"
                            inputMode="numeric"
                            value={withdrawCardNumber}
                            onChange={(e) => setWithdrawCardNumber(formatCardInput(e.target.value))}
                            placeholder="8600 **** **** ****"
                            maxLength={19}
                            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06]
                              text-text text-sm font-mono tracking-wider placeholder:text-text-hint/30
                              focus:outline-none focus:border-primary/30"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-text-hint mb-1 block">Ism familiya</label>
                        <input
                          type="text"
                          value={withdrawCardHolder}
                          onChange={(e) => setWithdrawCardHolder(e.target.value)}
                          placeholder="ESHMATOV TOSHMAT"
                          className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06]
                            text-text text-sm placeholder:text-text-hint/30
                            focus:outline-none focus:border-primary/30 uppercase"
                        />
                      </div>
                      {withdrawError && (
                        <div className="flex items-center gap-1.5 text-danger">
                          <AlertCircle size={12} />
                          <p className="text-xs">{withdrawError}</p>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          fullWidth
                          onClick={() => setShowWithdrawForm(false)}
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button
                          variant="primary"
                          fullWidth
                          onClick={handleWithdraw}
                          isLoading={withdrawMutation.isPending}
                        >
                          {t('referral.withdraw')}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Promo Code */}
              <div className="mt-5">
                <h3 className="text-sm font-semibold text-text mb-2">{t('referral.yourCode')}</h3>
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] font-mono text-sm text-primary tracking-wider">
                    {referralCode}
                  </div>
                  <button
                    onClick={() => void handleCopy(referralCode)}
                    className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.06] transition-colors"
                  >
                    <Copy size={16} className="text-text-hint" />
                  </button>
                </div>
              </div>

              {/* Share Link */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-text mb-2">{t('referral.shareLink')}</h3>
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-xs text-text-hint truncate">
                    {referralLink}
                  </div>
                  <button
                    onClick={() => void handleCopy(referralLink)}
                    className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.06] transition-colors"
                  >
                    <Copy size={16} className="text-text-hint" />
                  </button>
                </div>
                <Button variant="primary" fullWidth className="mt-3" onClick={handleShare}>
                  <Share2 size={16} className="mr-2" />
                  {t('referral.shareVia')}
                </Button>
              </div>

              {/* How it works */}
              <div className="mt-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
                <h3 className="text-sm font-semibold text-text mb-3">{t('referral.howItWorks')}</h3>
                <div className="space-y-3">
                  {[
                    { icon: <Share2 size={14} />, text: t('referral.step1') },
                    { icon: <Users size={14} />, text: t('referral.step2') },
                    { icon: <Gift size={14} />, text: t('referral.step3') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-primary">{i + 1}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-text-hint mt-0.5">{item.icon}</span>
                        <p className="text-sm text-text/80 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Invited Friends */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-text">{t('referral.invitedFriends')}</h3>
                  {friends && friends.length > 0 && (
                    <div className="flex gap-1 text-[10px]">
                      <button
                        onClick={() => setFriendsTab('all')}
                        className={`px-2 py-1 rounded-md transition-colors ${
                          friendsTab === 'all' ? 'bg-white/[0.08] text-text' : 'text-text-hint'
                        }`}
                      >
                        {t('common.all')}
                      </button>
                      <button
                        onClick={() => setFriendsTab('monthly')}
                        className={`px-2 py-1 rounded-md transition-colors ${
                          friendsTab === 'monthly' ? 'bg-white/[0.08] text-text' : 'text-text-hint'
                        }`}
                      >
                        Premium
                      </button>
                    </div>
                  )}
                </div>

                {friendsLoading ? (
                  <div className="flex justify-center py-4"><LoadingSpinner size="sm" /></div>
                ) : !filteredFriends.length ? (
                  <div className="py-8 text-center text-text-hint text-sm rounded-2xl bg-white/[0.02] border border-dashed border-white/[0.06]">
                    {t('referral.noFriendsYet')}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredFriends.map((friend) => (
                      <div
                        key={friend.id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]"
                      >
                        <div className="w-8 h-8 rounded-full bg-white/[0.06] flex items-center justify-center">
                          <Users size={14} className="text-text-hint" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-text-hint">
                            {format(new Date(friend.created_at), 'dd.MM.yyyy')}
                          </p>
                        </div>
                        {friend.status === 'active' ? (
                          <div className="flex items-center gap-1.5">
                            <Badge variant="success" className="text-[9px]">Premium</Badge>
                            <span className="text-xs font-medium text-success">
                              +{formatPrice(friend.bonus_amount)} UZS
                            </span>
                          </div>
                        ) : (
                          <Badge variant="glass" className="text-[9px]">{t('common.free')}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
